import type {
  DealFormData,
  WhitelistEntry,
  FlexibleSchedule,
  UrlItem,
  ReferralEntry,
} from './types'
import {
  CHAIN_ID_MAP,
  ERC4626_DEAL_TYPE,
  ERC4626_SALE_TYPE,
  V1_DEPOSIT_DEAL_TYPE,
  V1_DEPOSIT_SALE_TYPE,
  V1_TVL_DEAL_TYPE,
  V1_TVL_SALE_TYPE,
  V1_TVL_REWARD_KIND_OPTIONS,
  isV1ContractDealType,
  REQUEST_BESPOKE_VALUE,
  LOCKDROPS_VALUE,
  SALES_TYPE_OPTIONS,
  DEPOSIT_CHAIN_STABLECOINS,
} from './constants'
import { getDefaultDealFormData } from './form-defaults'
import {
  scaleByDecimals,
  priceToWeiString,
  monthsToSeconds,
  percentToBips,
} from './wei-scaling'

const ADMIN_DEALS_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/v2/admin/deals`

const CHAIN_NAME_BY_ID = Object.entries(CHAIN_ID_MAP).reduce<
  Record<number, string>
>((acc, [name, id]) => {
  acc[id] = name
  return acc
}, {})

const normalizeSalesTypeKey = (value?: string | null) =>
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const normalizeSalesTypeInput = (
  value?: string | null,
): DealFormData['salesType'] => {
  if (!value) return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  const normalized = normalizeSalesTypeKey(trimmed)
  const match = SALES_TYPE_OPTIONS.find((option) => {
    if (option.value === REQUEST_BESPOKE_VALUE) return false
    const valueKey = normalizeSalesTypeKey(option.value)
    const labelKey = normalizeSalesTypeKey(option.label)
    return normalized === valueKey || normalized === labelKey
  })
  return match?.value || REQUEST_BESPOKE_VALUE
}

const getSalesTypeValue = (value: DealFormData['salesType'] | ''): string => {
  const trimmed = (value || '').trim()
  if (!trimmed) {
    const defaultOption = SALES_TYPE_OPTIONS.find(
      (option) => option.value !== REQUEST_BESPOKE_VALUE,
    )
    return defaultOption?.value || 'Public Sale'
  }
  const normalized = normalizeSalesTypeKey(trimmed)
  const match = SALES_TYPE_OPTIONS.find((option) => {
    if (option.value === REQUEST_BESPOKE_VALUE) return false
    const valueKey = normalizeSalesTypeKey(option.value)
    const labelKey = normalizeSalesTypeKey(option.label)
    return normalized === valueKey || normalized === labelKey
  })
  return match?.value || REQUEST_BESPOKE_VALUE
}

const PRIVATE_SALES_TYPE_KEY = normalizeSalesTypeKey(
  SALES_TYPE_OPTIONS.find((option) => {
    if (option.value === REQUEST_BESPOKE_VALUE) return false
    return normalizeSalesTypeKey(option.value).includes('privatesale')
  })?.value || 'Private Sale',
)

const isPrivateSalesType = (value?: string | null) => {
  const normalized = normalizeSalesTypeKey(value || '')
  return normalized === PRIVATE_SALES_TYPE_KEY
}

const fromScaledValue = (
  value: string | number | null | undefined,
  decimals = 18,
): string => {
  if (value === null || value === undefined) return ''
  let raw = String(value).trim()
  if (!raw) return ''
  let sign = ''
  if (raw.startsWith('-')) {
    sign = '-'
    raw = raw.slice(1)
  }
  if (!/^\d+$/.test(raw)) raw = raw.replace(/[^0-9]/g, '')
  if (!raw) return ''
  if (decimals <= 0) {
    const normalized = raw.replace(/^0+(?=\d)/, '') || '0'
    return `${sign}${normalized}`
  }
  if (raw.length <= decimals) {
    const padded = raw.padStart(decimals, '0')
    const fraction = padded.replace(/0+$/, '')
    return fraction ? `${sign}0.${fraction}` : `${sign}0`
  }
  const integerPart =
    raw.slice(0, raw.length - decimals).replace(/^0+(?=\d)/, '') || '0'
  const fractionPart = raw.slice(raw.length - decimals).replace(/0+$/, '')
  return fractionPart
    ? `${sign}${integerPart}.${fractionPart}`
    : `${sign}${integerPart}`
}

const secondsToMonthsString = (
  seconds: number | string | null | undefined,
): string => {
  if (seconds === null || seconds === undefined) return ''
  const secNumber = Number(seconds)
  if (!Number.isFinite(secNumber) || secNumber === 0) return ''
  const months = secNumber / (30 * 24 * 60 * 60)
  const rounded = Number(months.toFixed(2))
  return Number.isInteger(rounded) ? String(rounded) : rounded.toString()
}

const bipsToPercentString = (
  value: number | string | null | undefined,
): string => {
  if (value === null || value === undefined) return ''
  const num = Number(value)
  if (!Number.isFinite(num) || num === 0) return ''
  const percent = num / 100
  const rounded = Number(percent.toFixed(2))
  return Number.isInteger(rounded) ? String(rounded) : rounded.toString()
}

const toDateTimeLocalString = (input?: string | null): string => {
  if (!input) return ''
  const date = new Date(input)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 16)
}

const deriveTgeDateFromPurchaseEnd = (
  purchaseEndDate?: string | null,
): string => {
  if (!purchaseEndDate) return ''
  const endDate = new Date(purchaseEndDate + 'Z')
  if (Number.isNaN(endDate.getTime())) return ''
  endDate.setUTCFullYear(endDate.getUTCFullYear() + 1)
  return endDate.toISOString().slice(0, 16)
}

const deriveTokenSupply = (fdv: string, tokenPrice: string): string => {
  const fdvNum = Number(fdv)
  const priceNum = Number(tokenPrice)
  if (!Number.isFinite(fdvNum) || !Number.isFinite(priceNum) || priceNum === 0)
    return ''
  const supply = fdvNum / priceNum
  if (!Number.isFinite(supply) || supply <= 0) return ''
  return supply % 1 === 0
    ? Math.round(supply).toLocaleString('en-US')
    : supply.toFixed(2).replace(/\.00$/, '')
}

const normalizeStatus = (
  status: string | undefined,
): DealFormData['submissionStatus'] => {
  if (!status) return 'under_review'
  const normalized = status.toLowerCase()
  if (
    normalized === 'draft' ||
    normalized === 'submitted' ||
    normalized === 'under_review'
  )
    return normalized
  return 'under_review'
}

export const normalizeDealState = (
  state: unknown,
): DealFormData['dealState'] => {
  if (!state || typeof state !== 'string') return null
  const upper = state.toUpperCase()
  if (
    upper === 'DRAFT' ||
    upper === 'SUBMITTED' ||
    upper === 'REQUESTED_CHANGE' ||
    upper === 'APPROVED'
  )
    return upper
  return null
}

export const formatDealStateLabel = (
  state: DealFormData['dealState'],
): string | null => {
  switch (state) {
    case 'DRAFT':
      return 'Draft'
    case 'SUBMITTED':
      return 'Submitted'
    case 'REQUESTED_CHANGE':
      return 'Change Requested'
    case 'APPROVED':
      return 'Approved'
    default:
      return null
  }
}

const extractFileName = (url: string, fallback: string) => {
  try {
    const parsed = new URL(url)
    const pathname = parsed.pathname
    const lastSegment = pathname.split('/').filter(Boolean).pop()
    if (lastSegment) return decodeURIComponent(lastSegment)
  } catch (error) {}
  return fallback
}

type RemoteFileStub = File & { serverUrl?: string; serverSize?: number }

const createRemoteFileStub = (
  url?: string,
  fallbackName = 'document',
  mimeType = 'application/octet-stream',
  sizeBytes?: number,
): RemoteFileStub | null => {
  if (!url || typeof File === 'undefined') return null
  try {
    const name = extractFileName(url, fallbackName)
    const file = new File([], name, { type: mimeType }) as RemoteFileStub
    file.serverUrl = url
    if (Number.isFinite(sizeBytes)) file.serverSize = sizeBytes
    return file
  } catch (error) {
    return null
  }
}

const SOCIAL_URL_KEYS = new Set<UrlItem['type']>([
  'youtube',
  'tiktok',
  'facebook',
  'instagram',
  'reddit',
  'telegram',
])

const escapeCsvValue = (value: string | number) =>
  `"${String(value ?? '').replace(/"/g, '""')}"`

const createCsvFileStubFromEntries = (
  entries: WhitelistEntry[] | undefined,
  filename: string,
  sizeBytes?: number,
): RemoteFileStub | null => {
  if (!Array.isArray(entries) || entries.length === 0) return null
  if (typeof File === 'undefined') return null
  const header = 'walletAddress,allocationAmount'
  const rows = entries.map((entry) =>
    [
      escapeCsvValue(entry.walletAddress || ''),
      escapeCsvValue(entry.allocationAmount ?? ''),
    ].join(','),
  )
  const csvString = [header, ...rows].join('\n')
  try {
    const file = new File([csvString], filename, {
      type: 'text/csv;charset=utf-8',
    }) as RemoteFileStub
    file.serverUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(
      csvString,
    )}`
    file.serverSize = Number(sizeBytes)
    return file
  } catch (error) {
    return null
  }
}

const createReferralCsvStub = (
  entries: ReferralEntry[] | undefined,
  filename: string,
  sizeBytes?: number,
): RemoteFileStub | null => {
  if (!Array.isArray(entries) || entries.length === 0) return null
  if (typeof File === 'undefined') return null
  const header = [
    'Promo Tier',
    'T2 Wallet Address',
    'T3 Wallet Address',
    'Discount %',
    'Kickback %',
    'Promo Code',
    'T2 Share %',
    'T3 Share %',
    'Name',
    'Telegram Contact',
  ].join(',')
  const rows = entries.map((entry) =>
    [
      escapeCsvValue(entry.promoTier || ''),
      escapeCsvValue(entry.t2WalletAddress || ''),
      escapeCsvValue(entry.t3WalletAddress || ''),
      escapeCsvValue(entry.discountPercentage ?? ''),
      escapeCsvValue(entry.kickbackPercentage ?? ''),
      escapeCsvValue(entry.promoCode || ''),
      escapeCsvValue(entry.t2SharePercentage ?? ''),
      escapeCsvValue(entry.t3SharePercentage ?? ''),
      escapeCsvValue(entry.contactName || ''),
      escapeCsvValue(entry.contactTelegram || ''),
    ].join(','),
  )
  const csvString = [header, ...rows].join('\n')
  try {
    const file = new File([csvString], filename, {
      type: 'text/csv;charset=utf-8',
    }) as RemoteFileStub
    file.serverUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(
      csvString,
    )}`
    file.serverSize = Number(sizeBytes)
    return file
  } catch (error) {
    return null
  }
}

export type DealType = 'fixed' | 'flexible'

export type Erc4626DealMetadata = {
  type: 'deposit'
  saleType: typeof ERC4626_SALE_TYPE
  depositChainId: number
  depositAddress: string
  depositToken: {
    address: string
    decimals: number
    name: string
    symbol: string
  }
  depositScheduleTBD: boolean
  hideTimeline: boolean
  requiresKyc: boolean
  vault: {
    standard: 'ERC4626'
    protocolName: string
    externalUrl?: string
  }
}

type TokenMetadata = {
  address: string
  decimals: number
  name: string
  symbol: string
}

export type V1DepositConfig = {
  depositAddress: string
  depositChainId: number
  depositToken: TokenMetadata
  trackId: number
}

export type V1TvlRewardModel =
  | {
      kind: 'percentPerMonth'
      rewardsPerMonth?: number
      lockPeriodMonths?: number
      fdv?: number
      totalSupply?: number
    }
  | {
      kind: 'fixedRatio'
      tokensPerDepositUnit?: number
      // Optional; both together enable the storefront FDV simulator.
      fdv?: number
      totalSupply?: number
    }
  | { kind: 'unquantified'; label: string }
  | { kind: 'weeklyPool'; weeks: V1TvlWeeklyPoolWeek[] }

export type V1TvlWeeklyPoolWeek = {
  baseUsd: number
  // Replaces baseUsd once total TVL reaches the threshold.
  boost?: { tvlThresholdUsd: number; usd: number }
}

export type V1TvlMetadata = {
  targetTVL?: number
  lockPeriodMonths?: number
  nativeYieldAPR?: number
  fees: number
  earlyExitPenalty: number
  earlyExitTokenSymbol?: string
  rewardToken?: {
    symbol?: string
    fdv?: number
    totalSupply?: number
    rewardsPerMonth?: number
  }
  // Read by the storefront in preference to the legacy rewardToken numbers.
  rewardModel?: V1TvlRewardModel
  vestingDescription?: string
}

export type V1DepositMetadata = {
  type: typeof V1_DEPOSIT_SALE_TYPE
  // Whether depositors must pass KYC. The storefront treats a missing flag
  // differently per deal layout, so new deals always state it.
  requiresKyc?: boolean
  // TVL deals only.
  saleType?: typeof V1_TVL_SALE_TYPE
  depositTokenPrice?: string
  tvl?: V1TvlMetadata
  // Multichain deals: every deposit chain, with the top-level one first.
  depositConfigs?: V1DepositConfig[]
  saleId?: number
  allocationSaleId?: number
  trackId: number
  depositChainId: number
  depositAddress: string
  depositToken: TokenMetadata
  depositStartTime: string
  depositEndTime: string
  // Optional sections: some sales are deposit-only.
  depositPoolSize?: string
  refundStartTime?: string
  refundEndTime?: string
  claimChainId?: number
  claimAddress?: string
  claimToken?: { decimals: number; symbol: string }
  saleToken?: TokenMetadata
}

const toOptionalInt = (value: unknown): number | undefined => {
  const text = String(value ?? '').trim()
  if (!text) return undefined
  const n = Number(text)
  return Number.isSafeInteger(n) ? n : undefined
}

const toUtcIso = (value: unknown): string => {
  const text = String(value ?? '').trim()
  if (!text) return ''
  const date = new Date(`${text}Z`)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString()
}

const trimmed = (value: unknown) => String(value ?? '').trim()

const toOptionalNumber = (value: unknown): number | undefined => {
  const text = trimmed(value)
  if (!text) return undefined
  const n = Number(text)
  return Number.isFinite(n) ? n : undefined
}

const withoutUndefined = <T extends Record<string, unknown>>(value: T): T =>
  Object.fromEntries(
    Object.entries(value).filter(([, v]) => v !== undefined),
  ) as T

/**
 * Splits stored `depositConfigs` into the entry mirroring the top-level
 * deposit fields and the additional chains, in stored order. The form edits
 * the additional chains by index, so loader and builder must agree on this.
 */
const splitV1DepositConfigs = (
  v1?: Partial<V1DepositMetadata> | null,
): { primary?: V1DepositConfig; additional: V1DepositConfig[] } => {
  const configs = Array.isArray(v1?.depositConfigs) ? v1!.depositConfigs! : []
  const primaryIndex = configs.findIndex(
    (config) =>
      config?.depositChainId === v1?.depositChainId &&
      trimmed(config?.depositAddress).toLowerCase() ===
        trimmed(v1?.depositAddress).toLowerCase(),
  )
  return {
    primary: configs[primaryIndex],
    additional: configs.filter((_, index) => index !== primaryIndex),
  }
}

const buildV1TvlRewardModel = (
  form: DealFormData,
): V1TvlRewardModel | undefined => {
  const fdv = toOptionalNumber(form.v1TvlRewardTokenFdv)
  const totalSupply = toOptionalNumber(form.v1TvlRewardTokenTotalSupply)
  switch (form.v1TvlRewardKind) {
    case 'percentPerMonth':
      return withoutUndefined({
        kind: 'percentPerMonth',
        rewardsPerMonth: toOptionalNumber(form.v1TvlRewardsPerMonth),
        lockPeriodMonths: toOptionalNumber(form.v1TvlLockPeriodMonths),
        fdv,
        totalSupply,
      })
    case 'fixedRatio':
      return withoutUndefined({
        kind: 'fixedRatio',
        tokensPerDepositUnit: toOptionalNumber(form.v1TvlTokensPerDepositUnit),
        fdv,
        totalSupply,
      })
    case 'unquantified':
      return { kind: 'unquantified', label: trimmed(form.v1TvlRewardLabel) }
    case 'weeklyPool':
      return {
        kind: 'weeklyPool',
        weeks: (form.v1TvlWeeklyPool ?? []).map((week) => {
          const tvlThresholdUsd = toOptionalNumber(week.boostTvlThresholdUsd)
          const usd = toOptionalNumber(week.boostUsd)
          return {
            baseUsd: toOptionalNumber(week.baseUsd) ?? 0,
            ...(tvlThresholdUsd !== undefined && usd !== undefined
              ? { boost: { tvlThresholdUsd, usd } }
              : {}),
          }
        }),
      }
    default:
      return undefined
  }
}

const buildV1TvlTerms = (form: DealFormData): V1TvlMetadata => {
  const earlyExitTokenSymbol = trimmed(form.v1TvlEarlyExitTokenSymbol)
  const vestingDescription = trimmed(form.v1TvlVestingDescription)
  const rewardModel = buildV1TvlRewardModel(form)
  // The legacy rate fields are kept in step with a percent-per-month model so
  // storefront builds that predate `rewardModel` show the same numbers.
  const rewardToken = withoutUndefined({
    symbol: trimmed(form.v1TvlRewardTokenSymbol) || undefined,
    ...(rewardModel?.kind === 'percentPerMonth'
      ? {
          fdv: rewardModel.fdv,
          totalSupply: rewardModel.totalSupply,
          rewardsPerMonth: rewardModel.rewardsPerMonth,
        }
      : {}),
  })

  return withoutUndefined({
    targetTVL: toOptionalNumber(form.v1TvlTargetTVL),
    lockPeriodMonths: toOptionalNumber(form.v1TvlLockPeriodMonths),
    nativeYieldAPR: toOptionalNumber(form.v1TvlNativeYieldAPR),
    fees: toOptionalNumber(form.v1TvlFees) ?? 0,
    earlyExitPenalty: toOptionalNumber(form.v1TvlEarlyExitPenalty) ?? 0,
    earlyExitTokenSymbol: earlyExitTokenSymbol || undefined,
    rewardToken: Object.keys(rewardToken).length ? rewardToken : undefined,
    rewardModel,
    vestingDescription: vestingDescription || undefined,
  })
}

/**
 * Builds the legacy v1 deposit-sale contract block. When `existing` is given
 * (edit mode) the identifying fields — sale IDs, chain IDs, and every
 * address — are copied from the stored deal so a form edit can never
 * repoint a live sale. Stored deposit chains are likewise fixed and cannot be
 * dropped, though new chains may be appended. Edits require no field, so a
 * blank deposit window or token detail falls back to the stored value.
 * Optional sections are omitted when left blank.
 */
export const buildV1DepositMetadata = (
  form: DealFormData,
  existing?: Partial<V1DepositMetadata> | null,
): V1DepositMetadata => {
  const locked = existing ?? null
  const pick = <T>(stored: T | undefined, fromForm: T): T =>
    locked && stored !== undefined ? stored : fromForm
  // Multichain deals (TVL, or any deal already storing depositConfigs) may
  // repoint deposit contracts and tokens after creation; a blank field keeps
  // the stored address.
  const addressesEditable =
    form.salesType === V1_TVL_DEAL_TYPE || Array.isArray(locked?.depositConfigs)
  const pickAddress = (stored: string | undefined, fromForm: string) =>
    addressesEditable ? fromForm || stored || '' : pick(stored, fromForm)

  const saleId = pick(locked?.saleId, toOptionalInt(form.v1SaleId))
  const allocationSaleId = pick(
    locked?.allocationSaleId,
    toOptionalInt(form.v1AllocationSaleId),
  )
  // Edit mode only: a blank value keeps what is stored.
  const orStored = <T>(fromForm: T, stored: T | undefined): T =>
    locked &&
    stored !== undefined &&
    (fromForm === '' || Number.isNaN(fromForm))
      ? stored
      : fromForm
  const toDecimals = (value: unknown) =>
    trimmed(value) ? Number(value) : Number.NaN
  const depositPoolSize = trimmed(form.v1DepositPoolSize)
  const refundStartTime = toUtcIso(form.v1RefundStartTime)
  const refundEndTime = toUtcIso(form.v1RefundEndTime)

  const claimChainId = pick(
    locked?.claimChainId,
    toOptionalInt(form.v1ClaimChainId),
  )
  const claimAddress = pick(locked?.claimAddress, trimmed(form.v1ClaimAddress))
  const hasClaim =
    claimChainId !== undefined ||
    Boolean(claimAddress) ||
    Boolean(trimmed(form.v1ClaimTokenSymbol))

  const saleTokenAddress = pick(
    locked?.saleToken?.address,
    trimmed(form.v1SaleTokenAddress),
  )
  const hasSaleToken =
    Boolean(saleTokenAddress) ||
    Boolean(trimmed(form.v1SaleTokenName)) ||
    Boolean(trimmed(form.v1SaleTokenSymbol))

  const primary: V1DepositConfig = {
    depositAddress: pickAddress(
      locked?.depositAddress,
      trimmed(form.v1DepositAddress),
    ),
    depositChainId: pick(
      locked?.depositChainId,
      toOptionalInt(form.v1DepositChainId) ?? 0,
    ),
    depositToken: {
      address: pickAddress(
        locked?.depositToken?.address,
        trimmed(form.depositTokenAddress),
      ),
      decimals: orStored(
        toDecimals(form.depositTokenDecimals),
        locked?.depositToken?.decimals,
      ),
      name: orStored(
        trimmed(form.depositTokenName),
        locked?.depositToken?.name,
      ),
      symbol: orStored(
        trimmed(form.depositTokenSymbol),
        locked?.depositToken?.symbol,
      ),
    },
    trackId: pick(locked?.trackId, toOptionalInt(form.v1TrackId) ?? 0),
  }

  const storedConfigs = splitV1DepositConfigs(locked)
  const formConfigs = form.v1DepositConfigs ?? []
  const additionalConfigs: V1DepositConfig[] = [
    ...formConfigs.map((row, index) => {
      const stored = storedConfigs.additional[index]
      return {
        ...stored,
        depositAddress: pickAddress(
          stored?.depositAddress,
          trimmed(row.depositAddress),
        ),
        depositChainId: pick(
          stored?.depositChainId,
          toOptionalInt(row.chainId) ?? 0,
        ),
        depositToken: {
          address: pickAddress(
            stored?.depositToken?.address,
            trimmed(row.tokenAddress),
          ),
          decimals: orStored(
            toDecimals(row.tokenDecimals),
            stored?.depositToken?.decimals,
          ),
          name: orStored(trimmed(row.tokenName), stored?.depositToken?.name),
          symbol: orStored(
            trimmed(row.tokenSymbol),
            stored?.depositToken?.symbol,
          ),
        },
        trackId: pick(stored?.trackId, toOptionalInt(row.trackId) ?? 0),
      }
    }),
    ...storedConfigs.additional.slice(formConfigs.length),
  ]
  const hasDepositConfigs =
    additionalConfigs.length > 0 || Array.isArray(locked?.depositConfigs)

  const isTvl = form.salesType === V1_TVL_DEAL_TYPE
  // Editing a deal stored without the flag must not switch KYC on by merely
  // saving it, so the flag is only added there once it is turned off.
  const requiresKyc = form.requiresKyc ?? true
  const writeRequiresKyc =
    !locked || locked.requiresKyc !== undefined || !requiresKyc
  const depositTokenPrice = trimmed(form.v1DepositTokenPrice)

  return {
    type: V1_DEPOSIT_SALE_TYPE,
    ...(isTvl ? { saleType: V1_TVL_SALE_TYPE } : {}),
    ...(writeRequiresKyc ? { requiresKyc } : {}),
    ...(saleId !== undefined ? { saleId } : {}),
    ...(allocationSaleId !== undefined ? { allocationSaleId } : {}),
    trackId: primary.trackId,
    depositChainId: primary.depositChainId,
    depositAddress: primary.depositAddress,
    depositToken: primary.depositToken,
    ...(hasDepositConfigs
      ? {
          depositConfigs: [
            { ...storedConfigs.primary, ...primary },
            ...additionalConfigs,
          ],
        }
      : {}),
    depositStartTime: orStored(
      toUtcIso(form.v1DepositStartTime),
      locked?.depositStartTime,
    ),
    depositEndTime: orStored(
      toUtcIso(form.v1DepositEndTime),
      locked?.depositEndTime,
    ),
    ...(depositPoolSize ? { depositPoolSize } : {}),
    ...(isTvl && depositTokenPrice ? { depositTokenPrice } : {}),
    ...(isTvl ? { tvl: buildV1TvlTerms(form) } : {}),
    ...(refundStartTime ? { refundStartTime } : {}),
    ...(refundEndTime ? { refundEndTime } : {}),
    ...(hasClaim
      ? {
          claimChainId: claimChainId ?? 0,
          claimAddress,
          claimToken: {
            decimals: Number(form.v1ClaimTokenDecimals),
            symbol: trimmed(form.v1ClaimTokenSymbol),
          },
        }
      : {}),
    ...(hasSaleToken
      ? {
          saleToken: {
            address: saleTokenAddress,
            decimals: Number(form.v1SaleTokenDecimals),
            name: trimmed(form.v1SaleTokenName),
            symbol: trimmed(form.v1SaleTokenSymbol),
          },
        }
      : {}),
  }
}

export type DealPayload = {
  type: number
  metadata: {
    overview: {
      salesType: {
        type: string
        detail: string
      }
      [key: string]: unknown
    }
    v1?: Erc4626DealMetadata | V1DepositMetadata
    timelines?: Array<{ type: string; datetime: string }>
    [key: string]: unknown
  }
  is_visible: boolean
  approved: boolean
}

const buildTimelineData = (
  form: DealFormData,
  normalizedSalesType: string,
): {
  timelines: Array<{ type: string; datetime: string }>
  tgeDate: Date | null
  nftTransferableDate: Date | null
} => {
  const derivedTgeTargetDate =
    form.tgeTargetDate || deriveTgeDateFromPurchaseEnd(form.purchaseEndDate)

  const timelines = [] as Array<{ type: string; datetime: string }>
  if (form.purchaseStartDate)
    timelines.push({
      type: 'depositStarts',
      datetime: new Date((form.purchaseStartDate as any) + 'Z').toISOString(),
    })
  if (normalizedSalesType !== 'OTC Deal' && form.purchaseEndDate) {
    const depositEndDate = new Date((form.purchaseEndDate as any) + 'Z')
    timelines.push({
      type: 'depositEnds',
      datetime: depositEndDate.toISOString(),
    })
  }
  if (normalizedSalesType !== 'OTC Deal' && form.mintStartDate) {
    timelines.push({
      type: 'nftClaiming',
      datetime: new Date((form.mintStartDate as any) + 'Z').toISOString(),
    })
  }
  if (normalizedSalesType !== 'OTC Deal' && derivedTgeTargetDate)
    timelines.push({
      type: 'firstUnlock',
      datetime: new Date((derivedTgeTargetDate as any) + 'Z').toISOString(),
    })
  if (form.vestingStartDate)
    timelines.push({
      type: 'vestingStarts',
      datetime: new Date((form.vestingStartDate as any) + 'Z').toISOString(),
    })
  const nftTransferability = (form as any).nftTransferability
  const customStart = (form as any).customStartDateTime
  const tgeDate = derivedTgeTargetDate
    ? new Date((derivedTgeTargetDate as any) + 'Z')
    : null
  let nftTransferableDate: Date | null = null
  if (nftTransferability === 'custom' && customStart) {
    const d = new Date((customStart as any) + 'Z')
    nftTransferableDate = d
    timelines.push({ type: 'nftTransferable', datetime: d.toISOString() })
  } else if (nftTransferability === 'immediate' && tgeDate) {
    nftTransferableDate = tgeDate
    timelines.push({ type: 'nftTransferable', datetime: tgeDate.toISOString() })
  } else if (nftTransferability === 'after-year' && tgeDate) {
    const plusOneYear = new Date(tgeDate)
    plusOneYear.setUTCFullYear(plusOneYear.getUTCFullYear() + 1)
    nftTransferableDate = plusOneYear
    timelines.push({
      type: 'nftTransferable',
      datetime: plusOneYear.toISOString(),
    })
  }

  return { timelines, tgeDate, nftTransferableDate }
}

export const buildTimelines = (
  form: DealFormData,
  normalizedSalesType: string,
) => buildTimelineData(form, normalizedSalesType).timelines

export const mapFormToPayload = (form: DealFormData): DealPayload => {
  const isFlexible = form.pricingMethod === 'flexible'

  const distributionChainId = CHAIN_ID_MAP[form.distributionChain] || 421614
  const depositChainId = CHAIN_ID_MAP[form.depositChain] || 421614

  const depositStablecoins = DEPOSIT_CHAIN_STABLECOINS[form.depositChain] || []
  const selectedStablecoin = depositStablecoins.find(
    (s) => s.value === form.depositChainStablecoin,
  )

  const paymentToken = selectedStablecoin
    ? {
        address: selectedStablecoin.address,
        symbol: selectedStablecoin.value,
        name: selectedStablecoin.label,
        decimals: selectedStablecoin.decimals,
      }
    : {
        address: '0x51c94B0C9d787d4E16c46b1630D5e791bf40F816',
        symbol: 'TUSD',
        name: 'TUSD',
        decimals: 18,
      }

  const discordUrl = (
    form.urls.find((u) => u.type === 'discord')?.url || ''
  ).trim()
  const urls = {
    website: form.urls.find((u) => u.type === 'website')?.url || '',
    x: form.urls.find((u) => u.type === 'x')?.url || '',
    ...(discordUrl ? { discord: discordUrl } : {}),
    other: form.urls
      .filter((u) => !['website', 'x', 'discord'].includes(u.type))
      .map((u) => ({ url: u.url.trim(), type: u.type }))
      .filter((u) => u.url),
  }

  const salesTypeRaw = ((form as any).salesType ||
    '') as DealFormData['salesType']
  const normalizedSalesType = getSalesTypeValue(salesTypeRaw)
  const salesTypeDetail =
    normalizedSalesType === REQUEST_BESPOKE_VALUE ||
    normalizedSalesType === 'OTC Deal' ||
    normalizedSalesType === LOCKDROPS_VALUE
      ? (form.salesTypeDetail || '').trim()
      : ''

  const salesTypePayload =
    normalizedSalesType === REQUEST_BESPOKE_VALUE
      ? {
          type: normalizedSalesType,
          detail: salesTypeDetail,
        }
      : {
          type: normalizedSalesType,
          detail: salesTypeDetail,
        }

  const overview = {
    projectName: form.projectName,
    projectMemo: (form as any).projectMemo || '',
    logoUrl: (form as any).logoUrl || '',
    bannerUrl: form.bannerUrl || '',
    categoryTags: form.category || [],
    urls,
    ...(normalizedSalesType !== 'OTC Deal' && {
      pitchDeck: {
        type: 'link',
        value: (form as any).pitchDeckUrl || form.researchReport || '',
      },
      researchReportUrl: form.researchReport || '',
      researchNotes: (form as any).researchNotes || '',
      requestResearchSupport: (form as any).requestResearchSupport || false,
      capTable: (form.investors || [])
        .map((n) => String(n || '').trim())
        .filter((n) => n.length > 0),
    }),
    salesType: salesTypePayload,
    ...(normalizedSalesType !== 'OTC Deal' && {
      jurisdiction: form.jurisdiction || 'other',
      jurisdictionOther:
        (form.jurisdiction || '') === 'other'
          ? (form.jurisdictionOther || '').trim()
          : '',
    }),
    projectSummary: {
      projectOverview: form.description || '',
      salesDetails: (form as any).salesDetails || '',
    },
  }

  if (isV1ContractDealType(normalizedSalesType)) {
    return {
      type: 0,
      metadata: {
        overview,
        v1: buildV1DepositMetadata(form),
        timelines: buildTimelines(form, normalizedSalesType),
      },
      is_visible: true,
      approved: false,
    }
  }

  if (normalizedSalesType === ERC4626_DEAL_TYPE) {
    const vaultExternalUrl = String(form.vaultExternalUrl || '').trim()
    const depositStartInput = String(form.purchaseStartDate || '').trim()
    const depositStartDate = new Date(`${depositStartInput}Z`)
    const depositStartsAt =
      depositStartInput && !Number.isNaN(depositStartDate.getTime())
        ? depositStartDate.toISOString()
        : null

    return {
      // Keep the deal API's existing top-level numeric discriminator.
      // Storefront routing comes from v1.type; Earn/Deals separation comes
      // only from the exact v1.saleType value below.
      type: 0,
      metadata: {
        overview,
        v1: {
          type: 'deposit',
          saleType: ERC4626_SALE_TYPE,
          depositChainId: Number(form.vaultChainId),
          depositAddress: String(form.vaultAddress || '').trim(),
          depositToken: {
            address: String(form.depositTokenAddress || '').trim(),
            decimals: Number(form.depositTokenDecimals),
            name: String(form.depositTokenName || '').trim(),
            symbol: String(form.depositTokenSymbol || '').trim(),
          },
          depositScheduleTBD: Boolean(form.depositScheduleTBD),
          hideTimeline: form.hideTimeline ?? true,
          requiresKyc: form.requiresKyc ?? true,
          vault: {
            standard: 'ERC4626',
            protocolName: String(form.vaultProtocolName || '').trim(),
            ...(vaultExternalUrl ? { externalUrl: vaultExternalUrl } : {}),
          },
        },
        timelines: depositStartsAt
          ? [{ type: 'depositStarts', datetime: depositStartsAt }]
          : [],
      },
      is_visible: true,
      approved: false,
    }
  }

  const toNumber = (val: string) => {
    const n = Number(String(val).replace(/[^0-9.]/g, ''))
    return isFinite(n) ? n : 0
  }

  const allocationExceedsSupply = (formPayload: DealFormData) => {
    const minRaise = toNumber(formPayload.minimumRaiseThreshold)
    if (!(minRaise > 0)) return false
    if (formPayload.pricingMethod === 'fixed') {
      const fdvValue = toNumber(formPayload.fdv)
      if (!(fdvValue > 0)) return false
      return minRaise > fdvValue
    }
    if (formPayload.pricingMethod === 'flexible') {
      const schedules = Array.isArray(formPayload.flexibleSchedules)
        ? (formPayload.flexibleSchedules as FlexibleSchedule[])
        : []
      if (!schedules.length) return false
      const reciprocalSum = schedules.reduce((sum, schedule) => {
        const scheduleFdv = toNumber(schedule.fdv)
        if (!(scheduleFdv > 0)) return sum
        return sum + 1 / scheduleFdv
      }, 0)
      if (!(reciprocalSum > 0)) return false
      return minRaise * reciprocalSum > 1
    }
    return false
  }

  const parseSupply = (
    s: string,
  ):
    | { type: 'single'; value: number }
    | { type: 'range'; min: number; max: number }
    | null => {
    if (!s) return null
    const clean = String(s).trim()
    if (clean.includes('-') || clean.includes('–')) {
      const [a, b] = clean
        .split(/[-–]/)
        .map((p) => Number(String(p).replace(/[^0-9.]/g, '')))
      if (isFinite(a) && isFinite(b)) {
        const min = Math.min(a, b)
        const max = Math.max(a, b)
        return { type: 'range', min, max }
      }
      return null
    }
    const v = Number(String(clean).replace(/[^0-9.]/g, ''))
    if (isFinite(v) && v > 0) return { type: 'single', value: v }
    return null
  }

  const whitelist = (form.whitelistEntries || []).map((w: WhitelistEntry) => ({
    walletAddress: w.walletAddress,
    allocationAmount: String(w.allocationAmount || ''),
  }))

  const projectToken = {
    address: form.projectTokenAddress || '',
    symbol: form.projectTokenSymbol || '',
    name: form.projectTokenName || '',
    decimals: Number(form.projectTokenDecimals || 0),
  }

  if (
    normalizedSalesType !== LOCKDROPS_VALUE &&
    allocationExceedsSupply(form)
  ) {
    throw new Error(
      'Total allocation exceeds total token supply. Reduce the minimum raise or adjust FDVs before submitting.',
    )
  }

  const supplyInfo = parseSupply(form.tokenSupply)
  const fdvNum = toNumber(form.fdv)
  const tokenPriceFixed = supplyInfo
    ? supplyInfo.type === 'single'
      ? fdvNum / supplyInfo.value
      : fdvNum / ((supplyInfo.min + supplyInfo.max) / 2)
    : 0
  const tokenDecimals = Number.isFinite(projectToken.decimals)
    ? (projectToken.decimals as number)
    : 18

  const pricingMethod = String((form as any).pricingMethod || '')

  const referralCustomEntries = Array.isArray(
    (form as any).referralCustomEntries,
  )
    ? ((form as any).referralCustomEntries as ReferralEntry[])
    : []

  const generalReferralDiscount = toNumber(
    (form as any).referralGeneralDiscount || '0',
  )
  const generalReferralKickback = toNumber(
    (form as any).referralGeneralKickback || '0',
  )

  const royaltyProjectPct = toNumber((form as any).royaltySplit?.project || '0')
  const royaltyCuratorPct = toNumber((form as any).royaltySplit?.curator || '0')
  const projectRoyaltyBips = percentToBips(royaltyProjectPct)
  const curatorRoyaltyBips = percentToBips(royaltyCuratorPct)
  const totalRoyaltyBips = projectRoyaltyBips + curatorRoyaltyBips

  const detail: any = {
    minRaiseThreshold: toNumber(form.minimumRaiseThreshold),
    minInvestmentPerUser: toNumber(form.minimumInvestmentPerUser),
    useMaxDeposit: false,
    withdrawalType: Number((form as any).withdrawalType ?? 0),
    whitelist,
    depositChainId: depositChainId,
    paymentToken,
    distributionChainId: distributionChainId,
    tokenSupply: form.tokenSupply || '',
    secondaryMarketEnabled: Boolean((form as any).secondaryMarketEnabled),
    ...(normalizedSalesType !== LOCKDROPS_VALUE ? { projectToken } : {}),
    ...(normalizedSalesType !== LOCKDROPS_VALUE
      ? {
          pricingBlob:
            pricingMethod === 'bespoke'
              ? {}
              : isFlexible
              ? {
                  schemaId: 1,
                  version: 2,
                  whitelist: { root: '0x' },
                  payload: {
                    maxCapInWei: scaleByDecimals(
                      String(form.maxRaise ?? ''),
                      paymentToken.decimals,
                    ),
                    tiers: (form.flexibleSchedules || []).map(
                      (s: FlexibleSchedule, idx: number) => {
                        const fdvTier = toNumber(s.fdv)
                        const tokenPriceTier = supplyInfo
                          ? supplyInfo.type === 'single'
                            ? fdvTier / supplyInfo.value
                            : fdvTier / ((supplyInfo.min + supplyInfo.max) / 2)
                          : 0
                        const exampleTierHex = [
                          '0x69c5260b6c25d016ea1bdcce46ff91bbf99435dcc9580fe55cc1177d0a79bd63',
                          '0xdad09e4fdef36c7331af8bec2a1ee304bdae0a3aed2cba014b89439ef12dfc30',
                          '0x96ede6a2c7a2660ae1925baeb6bfaecf686b98de75ddf4cad037530af2df72ee',
                        ]
                        const tgeBips = percentToBips(toNumber(s.tgeRelease))
                        return {
                          tierId: s.name || `Tier${idx + 1}`,
                          tierIdHex:
                            exampleTierHex[idx] || '0x' + '0'.repeat(64),
                          fdvInWei: scaleByDecimals(fdvTier, tokenDecimals),
                          tokenPriceInWei: priceToWeiString(
                            tokenPriceTier,
                            paymentToken.decimals,
                          ),
                          cliffSecs: monthsToSeconds(toNumber(s.cliff)),
                          vestingSecs: monthsToSeconds(
                            toNumber(s.linearVesting),
                          ),
                          tgeUnlockBips: tgeBips,
                          enableBuybackProtection:
                            s.enableBuybackProtection || false,
                          buybackPaymentToken: s.buybackPaymentToken || '',
                          buybackPrice: s.buybackPrice || '',
                          fundingWallet: s.fundingWallet || '',
                          buybackStartDate: s.buybackStartDate || '',
                          buybackEndDate: s.buybackEndDate || '',
                        }
                      },
                    ),
                  },
                }
              : {
                  schemaId: 0,
                  version: 1,
                  whitelist: { root: '0x' },
                  payload: {
                    tierId: form.fixedPoolName || 'Genesis Pool',
                    fdvInWei: scaleByDecimals(
                      String(form.fdv || ''),
                      tokenDecimals,
                    ),
                    maxCapInWei: scaleByDecimals(
                      String(form.maxRaise ?? ''),
                      paymentToken.decimals,
                    ),
                    tokenPriceInWei: priceToWeiString(
                      String(tokenPriceFixed || 0),
                      paymentToken.decimals,
                    ),
                    cliffSecs: monthsToSeconds(toNumber(form.cliff)),
                    vestingSecs: monthsToSeconds(toNumber(form.linearVesting)),
                    tgeUnlockBips: percentToBips(toNumber(form.tgeRelease)),
                    enableBuybackProtection:
                      form.enableBuybackProtection || false,
                    buybackPaymentToken: form.buybackPaymentToken || '',
                    buybackPrice: form.buybackPrice || '',
                    fundingWallet: form.fundingWallet || '',
                    buybackStartDate: form.buybackStartDate || '',
                    buybackEndDate: form.buybackEndDate || '',
                  },
                },
        }
      : {}),
    referral:
      normalizedSalesType === 'OTC Deal'
        ? {
            enabled: false,
            general: { discount: 0, kickback: 0 },
            customWallets: [],
          }
        : {
            enabled: Boolean((form as any).referralEnabled),
            general: {
              discount: generalReferralDiscount,
              kickback: generalReferralKickback,
            },
            customWallets: referralCustomEntries,
          },
    accessoryServices: (() => {
      const svc = (form as any).accessoryServices || {}
      const otherSelected = Boolean(svc.other)
      const otherText = otherSelected
        ? String(svc.otherDescription || '').trim()
        : ''
      return {
        marketing: Boolean(svc.marketing),
        kolCampaigns: Boolean(svc.kolCampaigns),
        marketMaking: Boolean(svc.marketMaking),
        treasuryManagement: Boolean(svc.treasuryManagement),
        otcDeals: Boolean(svc.otcDeals),
        auditing: Boolean(svc.auditing),
        hrRecruitment: Boolean(svc.hrRecruitment),
        cexListing: Boolean(svc.cexListing),
        other: otherText,
      }
    })(),
    referralDiscount:
      normalizedSalesType === 'OTC Deal' ? 0 : generalReferralDiscount,
    ...(normalizedSalesType === LOCKDROPS_VALUE
      ? {
          lockdropDuration: String(form.lockdropDuration || ''),
          lockdropStartDate: form.lockdropStartDate || '',
          lockdropEndDate: form.lockdropEndDate || '',
          targetTVL: toNumber(form.targetTVL),
          targetYieldProposal: String(form.targetYieldProposal || ''),
          fdvLastRound: toNumber(form.fdvLastRound),
          enableWhitelistAccess: Boolean(form.enableWhitelistAccess),
        }
      : {}),
  }

  const proofs: Array<{ type: string; value: string }> =
    normalizedSalesType === 'OTC Deal'
      ? []
      : [
          {
            type: 'onchain',
            value: form.transactionLink || '',
          },
        ]
  const supportingDocsUrl = (form as any).supportingDocumentsUrl || ''
  if (normalizedSalesType !== 'OTC Deal' && supportingDocsUrl)
    proofs.push({ type: 'document', value: supportingDocsUrl })

  const commissionType = form.commissionType || ''
  const curatorStablecoinFeeBips =
    commissionType === 'tokens'
      ? 0
      : percentToBips(toNumber(form.stablecoinCommission))
  const curatorTokenFeeBips =
    commissionType === 'stablecoins'
      ? 0
      : percentToBips(toNumber(form.tokenCommission))

  const funds =
    normalizedSalesType === 'OTC Deal'
      ? {
          curatorFeeBips: curatorStablecoinFeeBips,
          curatorWallet: form.curatorCommissionWallet || '',
          curatorCommissionType: commissionType,
          curatorStablecoinFeeBips,
          curatorTokenFeeBips,
          protocolFeeBips: 200,
          otcSettlementWallet: form.otcSettlementWallet || '',
        }
      : {
          projectReceivingWallet: form.projectWallet || '',
          curatorCommitmentAmount: scaleByDecimals(
            String(form.curatorCommitmentAmount || ''),
            paymentToken.decimals,
          ),
          curatorProofs: proofs,
          protocolFeeBips: 200,
          ...(normalizedSalesType === LOCKDROPS_VALUE
            ? {
                hasAssetManager: form.hasAssetManager || '',
                assetManagerWallet: form.assetManagerWallet || '',
              }
            : {}),
          ...(normalizedSalesType === LOCKDROPS_VALUE
            ? {
                lockdropCuratorWallet: form.lockdropCuratorWallet || '',
                lockdropPerformanceFeesType:
                  form.lockdropPerformanceFeesType || '',
                lockdropPerformanceFeesStablecoin:
                  form.lockdropPerformanceFeesStablecoin || '',
                lockdropPerformanceFeesToken:
                  form.lockdropPerformanceFeesToken || '',
                lockdropManagementFeesType:
                  form.lockdropManagementFeesType || '',
                lockdropManagementFeesStablecoin:
                  form.lockdropManagementFeesStablecoin || '',
                lockdropManagementFeesToken:
                  form.lockdropManagementFeesToken || '',
              }
            : {
                curatorFeeBips: curatorStablecoinFeeBips,
                curatorWallet: form.curatorCommissionWallet || '',
                curatorCommissionType: commissionType,
                curatorStablecoinFeeBips,
                curatorTokenFeeBips,
              }),
        }

  const derivedVisibility = isPrivateSalesType(normalizedSalesType)
    ? 'private'
    : 'public'
  const visibilityEligibility = {
    visibility: derivedVisibility,
    blockedCountries: (form.countryRestrictions || []).map((c) =>
      c.toUpperCase(),
    ),
    investorEligibility: ['KYC_nonAccredited'],
  }

  const { timelines, tgeDate, nftTransferableDate } = buildTimelineData(
    form,
    normalizedSalesType,
  )

  const tgeTimestamp =
    normalizedSalesType !== 'OTC Deal' && tgeDate
      ? Math.floor(tgeDate.getTime() / 1000)
      : 0
  const nonTransferableUntil =
    normalizedSalesType !== 'OTC Deal' && nftTransferableDate
      ? Math.floor(nftTransferableDate.getTime() / 1000)
      : 0

  const payload = {
    type: isFlexible ? 1 : 0,
    metadata: {
      overview,
      ...(normalizedSalesType !== 'OTC Deal'
        ? { detail }
        : {
            detail: {
              depositChainId: depositChainId,
              distributionChainId: distributionChainId,
            },
          }),
      funds,
      visibilityEligibility,
      timelines,
      ...(isPrivateSalesType(normalizedSalesType)
        ? {
            marketingGuidelinesAgreementAccepted:
              form.marketingGuidelinesAgreementAccepted,
          }
        : {}),
      nftMetadata: {
        name: 'IF Deal Token',
        symbol: 'IFD',
        tgeTimestamp,
        nonTransferableUntil,
        royaltyReceiver: '0x91e2304f860914348d7b1b1a91cf1cc3d3cf3211',
        royaltyBips: totalRoyaltyBips,
        royaltySplit: {
          projectBips: projectRoyaltyBips,
          curatorBips: curatorRoyaltyBips,
        },
      },
    },
    is_visible: true,
    approved: false,
  }

  return payload
}

export const mapDealResponseToFormData = (deal: any): DealFormData => {
  const metadata = deal?.metadata ?? {}
  const overview = metadata.overview ?? {}
  const v1 = metadata.v1 ?? {}
  const detail = metadata.detail ?? {}
  const funds = metadata.funds ?? {}
  const visibility = metadata.visibilityEligibility ?? {}
  const timelines = Array.isArray(metadata.timelines) ? metadata.timelines : []
  const nftMetadata = metadata.nftMetadata ?? {}
  const referral = detail.referral ?? {}
  const pricingBlob = detail.pricingBlob ?? {}
  const pricingPayload = pricingBlob?.payload ?? {}
  const projectToken = detail.projectToken ?? {}
  const paymentToken = detail.paymentToken ?? {}
  const whitelist = Array.isArray(detail.whitelist) ? detail.whitelist : []

  const formData = {
    ...getDefaultDealFormData(),
  } as DealFormData & Record<string, any>

  const projectTokenDecimals = Number(projectToken.decimals ?? 18)
  const paymentTokenDecimals = Number(paymentToken.decimals ?? 18)

  const urlsObj = overview.urls ?? {}
  const getUrlString = (value: unknown) =>
    typeof value === 'string' ? value : ''

  const websiteUrl = getUrlString((urlsObj as any).website)
  const xUrl = getUrlString((urlsObj as any).x ?? (urlsObj as any).twitter)
  const discordUrl = getUrlString((urlsObj as any).discord)

  const urls: UrlItem[] = [
    { id: 'website', type: 'website', url: websiteUrl },
    { id: 'x', type: 'x', url: xUrl },
    { id: 'discord', type: 'discord', url: discordUrl },
  ]

  const usedTypes = new Set<UrlItem['type']>(['website', 'x', 'discord'])
  SOCIAL_URL_KEYS.forEach((key) => {
    if (usedTypes.has(key)) return
    const value = getUrlString((urlsObj as Record<string, unknown>)[key])
    if (value) {
      urls.push({ id: key, type: key, url: value })
      usedTypes.add(key)
    }
  })

  const otherRaw = (urlsObj as any).other
  if (Array.isArray(otherRaw)) {
    otherRaw.forEach((value: unknown, index) => {
      if (
        typeof value === 'object' &&
        value !== null &&
        'url' in value &&
        'type' in value
      ) {
        const item = value as { url: string; type: string }
        const normalizedUrl = typeof item.url === 'string' ? item.url : ''
        const normalizedType =
          typeof item.type === 'string' ? item.type : 'custom'
        if (normalizedUrl) {
          urls.push({
            id: `other-${index}`,
            type: normalizedType as UrlItem['type'],
            url: normalizedUrl,
          })
        }
      } else {
        const normalized = getUrlString(value)
        if (normalized)
          urls.push({ id: `other-${index}`, type: 'custom', url: normalized })
      }
    })
  } else {
    const normalized = getUrlString(otherRaw)
    if (normalized)
      urls.push({ id: 'other-0', type: 'custom', url: normalized })
  }

  Object.entries(urlsObj).forEach(([key, value], index) => {
    const normalizedKey = key.toLowerCase()
    if (
      ['website', 'x', 'twitter', 'discord', 'other'].includes(normalizedKey) ||
      SOCIAL_URL_KEYS.has(normalizedKey as UrlItem['type'])
    )
      return
    const normalizedValue = getUrlString(value)
    if (normalizedValue)
      urls.push({
        id: `custom-${normalizedKey}-${index}`,
        type: 'custom',
        url: normalizedValue,
      })
  })

  formData.urls = urls
  formData.website = websiteUrl
  formData.twitter = xUrl
  formData.discord = discordUrl
  formData.otherUrl = Array.isArray(otherRaw)
    ? getUrlString(otherRaw[0])
    : getUrlString(otherRaw)

  const projectSummary = overview.projectSummary ?? {}
  const getSummaryString = (value: unknown) =>
    typeof value === 'string' ? value : ''
  const projectOverviewValue = getSummaryString(projectSummary.projectOverview)
  const salesDetailsValue = getSummaryString(projectSummary.salesDetails)
  formData.salesDetails = salesDetailsValue
  formData.projectName = overview.projectName ?? ''
  formData.slug = typeof deal?.slug === 'string' ? deal.slug : ''
  formData.description = projectOverviewValue
  formData.projectMemo = overview.projectMemo ?? ''
  formData.category = Array.isArray(overview.categoryTags)
    ? overview.categoryTags
    : []
  const salesTypeRaw = overview.salesType
  let normalizedSalesTypeFromOverview = ''
  let salesTypeDetailRaw = ''
  if (typeof salesTypeRaw === 'string') {
    normalizedSalesTypeFromOverview = normalizeSalesTypeInput(salesTypeRaw)
  } else if (
    salesTypeRaw &&
    typeof salesTypeRaw === 'object' &&
    typeof salesTypeRaw.type === 'string'
  ) {
    normalizedSalesTypeFromOverview = normalizeSalesTypeInput(salesTypeRaw.type)
    if (typeof salesTypeRaw.detail === 'string')
      salesTypeDetailRaw = salesTypeRaw.detail
  }
  if (!normalizedSalesTypeFromOverview)
    normalizedSalesTypeFromOverview = normalizeSalesTypeInput('')
  if (
    !salesTypeDetailRaw &&
    typeof overview.salesTypeDetail === 'string' &&
    overview.salesTypeDetail
  ) {
    salesTypeDetailRaw = overview.salesTypeDetail
  }
  const toText = (value: unknown) =>
    value === null || value === undefined ? '' : String(value)
  if (v1.type === V1_DEPOSIT_SALE_TYPE && v1.saleType !== ERC4626_SALE_TYPE) {
    formData.salesType =
      v1.saleType === V1_TVL_SALE_TYPE ? V1_TVL_DEAL_TYPE : V1_DEPOSIT_DEAL_TYPE
    formData.salesTypeDetail = ''
    formData.requiresKyc = v1.requiresKyc !== false
    formData.v1SaleId = toText(v1.saleId)
    formData.v1AllocationSaleId = toText(v1.allocationSaleId)
    formData.v1TrackId = toText(v1.trackId)
    formData.v1DepositChainId = toText(v1.depositChainId)
    formData.v1DepositAddress = v1.depositAddress ?? ''
    formData.depositTokenAddress = v1.depositToken?.address ?? ''
    formData.depositTokenDecimals = toText(v1.depositToken?.decimals)
    formData.depositTokenName = v1.depositToken?.name ?? ''
    formData.depositTokenSymbol = v1.depositToken?.symbol ?? ''
    formData.v1DepositPoolSize = toText(v1.depositPoolSize)
    formData.v1DepositStartTime = toDateTimeLocalString(v1.depositStartTime)
    formData.v1DepositEndTime = toDateTimeLocalString(v1.depositEndTime)
    formData.v1RefundStartTime = toDateTimeLocalString(v1.refundStartTime)
    formData.v1RefundEndTime = toDateTimeLocalString(v1.refundEndTime)
    formData.v1ClaimChainId = toText(v1.claimChainId)
    formData.v1ClaimAddress = v1.claimAddress ?? ''
    formData.v1ClaimTokenDecimals = toText(v1.claimToken?.decimals)
    formData.v1ClaimTokenSymbol = v1.claimToken?.symbol ?? ''
    formData.v1SaleTokenAddress = v1.saleToken?.address ?? ''
    formData.v1SaleTokenDecimals = toText(v1.saleToken?.decimals)
    formData.v1SaleTokenName = v1.saleToken?.name ?? ''
    formData.v1SaleTokenSymbol = v1.saleToken?.symbol ?? ''
    formData.v1DepositConfigs = splitV1DepositConfigs(v1).additional.map(
      (config) => ({
        chainId: toText(config?.depositChainId),
        depositAddress: config?.depositAddress ?? '',
        tokenAddress: config?.depositToken?.address ?? '',
        tokenName: config?.depositToken?.name ?? '',
        tokenSymbol: config?.depositToken?.symbol ?? '',
        tokenDecimals: toText(config?.depositToken?.decimals),
        trackId: toText(config?.trackId),
      }),
    )
    formData.v1DepositTokenPrice = toText(v1.depositTokenPrice)
    formData.v1TvlTargetTVL = toText(v1.tvl?.targetTVL)
    formData.v1TvlLockPeriodMonths = toText(v1.tvl?.lockPeriodMonths)
    formData.v1TvlNativeYieldAPR = toText(v1.tvl?.nativeYieldAPR)
    formData.v1TvlFees = toText(v1.tvl?.fees)
    formData.v1TvlEarlyExitPenalty = toText(v1.tvl?.earlyExitPenalty)
    formData.v1TvlEarlyExitTokenSymbol = v1.tvl?.earlyExitTokenSymbol ?? ''
    // Deals authored before `rewardModel` carry a percent-per-month rate in
    // the rewardToken fields; the storefront resolves them the same way.
    const rewardToken = v1.tvl?.rewardToken ?? {}
    const rewardModel =
      v1.tvl?.rewardModel ??
      (rewardToken.rewardsPerMonth !== undefined
        ? { kind: 'percentPerMonth', ...rewardToken }
        : {})
    formData.v1TvlRewardKind = V1_TVL_REWARD_KIND_OPTIONS.some(
      (option) => option.value === rewardModel.kind,
    )
      ? rewardModel.kind
      : ''
    formData.v1TvlRewardTokenSymbol = rewardToken.symbol ?? ''
    formData.v1TvlRewardTokenFdv = toText(rewardModel.fdv)
    formData.v1TvlRewardTokenTotalSupply = toText(rewardModel.totalSupply)
    formData.v1TvlRewardsPerMonth = toText(rewardModel.rewardsPerMonth)
    formData.v1TvlTokensPerDepositUnit = toText(
      rewardModel.tokensPerDepositUnit,
    )
    formData.v1TvlRewardLabel = rewardModel.label ?? ''
    formData.v1TvlWeeklyPool = (
      Array.isArray(rewardModel.weeks) ? rewardModel.weeks : []
    ).map((week: any) => ({
      baseUsd: toText(week?.baseUsd),
      boostTvlThresholdUsd: toText(week?.boost?.tvlThresholdUsd),
      boostUsd: toText(week?.boost?.usd),
    }))
    formData.v1TvlVestingDescription = v1.tvl?.vestingDescription ?? ''
  } else if (v1.saleType === ERC4626_SALE_TYPE) {
    formData.salesType = ERC4626_DEAL_TYPE
    formData.salesTypeDetail = ''
    formData.vaultChainId =
      v1.depositChainId === null || v1.depositChainId === undefined
        ? ''
        : String(v1.depositChainId)
    formData.vaultAddress = v1.depositAddress ?? ''
    formData.depositTokenAddress = v1.depositToken?.address ?? ''
    formData.depositTokenDecimals =
      v1.depositToken?.decimals === null ||
      v1.depositToken?.decimals === undefined
        ? ''
        : String(v1.depositToken.decimals)
    formData.depositTokenName = v1.depositToken?.name ?? ''
    formData.depositTokenSymbol = v1.depositToken?.symbol ?? ''
    formData.depositScheduleTBD = Boolean(v1.depositScheduleTBD)
    formData.hideTimeline = v1.hideTimeline ?? true
    formData.requiresKyc = v1.requiresKyc ?? true
    formData.vaultProtocolName = v1.vault?.protocolName ?? ''
    formData.vaultExternalUrl = v1.vault?.externalUrl ?? ''
  } else {
    formData.salesType = normalizedSalesTypeFromOverview
    if (
      normalizedSalesTypeFromOverview === REQUEST_BESPOKE_VALUE ||
      normalizedSalesTypeFromOverview === 'OTC Deal' ||
      normalizedSalesTypeFromOverview === LOCKDROPS_VALUE
    ) {
      formData.salesTypeDetail = salesTypeDetailRaw || ''
    } else {
      formData.salesTypeDetail = ''
    }
  }
  formData.researchReport = overview.researchReportUrl ?? ''
  formData.requestResearchSupport = Boolean(overview.requestResearchSupport)
  formData.researchNotes = overview.researchNotes ?? ''
  formData.researchPath = 'link'
  formData.researchMaterials = 'link'
  formData.requestResearch = Boolean(overview.requestResearchSupport)
  formData.investors =
    Array.isArray(overview.capTable) && overview.capTable.length
      ? overview.capTable
      : ['']
  formData.jurisdiction = overview.jurisdiction ?? ''
  formData.jurisdictionOther =
    typeof overview.jurisdictionOther === 'string'
      ? overview.jurisdictionOther
      : ''

  const logoUrl = typeof overview.logoUrl === 'string' ? overview.logoUrl : ''
  formData.logoUrl = logoUrl
  formData.logo = createRemoteFileStub(
    logoUrl,
    'logo.png',
    'image/png',
    (overview.logo as any)?.size,
  )

  const bannerUrl =
    typeof overview.bannerUrl === 'string' ? overview.bannerUrl : ''
  formData.bannerUrl = bannerUrl
  formData.banner = createRemoteFileStub(
    bannerUrl,
    'banner.png',
    'image/png',
    (overview.banner as any)?.size,
  )

  const pitchDeckUrl =
    typeof overview.pitchDeck?.value === 'string'
      ? overview.pitchDeck.value
      : ''
  const pitchDeckSize = Number(
    overview.pitchDeck?.size ?? overview.pitchDeck?.bytes,
  )
  formData.pitchDeckUrl = pitchDeckUrl
  formData.pitchDeck = createRemoteFileStub(
    pitchDeckUrl,
    'pitch-deck.pdf',
    'application/pdf',
    Number.isFinite(pitchDeckSize) ? pitchDeckSize : undefined,
  )

  formData.countryRestrictions = Array.isArray(visibility.blockedCountries)
    ? visibility.blockedCountries.map((c: string) => c.toLowerCase())
    : []

  formData.whitelistEntries = whitelist.map((entry) => ({
    walletAddress: String(entry.walletAddress || entry.wallet_address || ''),
    allocationAmount: (() => {
      const value = entry.allocationAmount ?? entry.allocation_amount ?? ''
      if (value === null || value === undefined) return ''
      if (typeof value === 'number') return String(value)
      const trimmed = String(value).trim()
      return trimmed
    })(),
  }))
  formData.enableWhitelistAccess = formData.whitelistEntries.length > 0
  formData.whitelistAddressCount = formData.whitelistEntries.length
  formData.whitelistCsv = createCsvFileStubFromEntries(
    formData.whitelistEntries,
    'whitelist.csv',
    detail.whitelistCsvSize ?? detail.whitelist_csv_size,
  )

  const distributionChainId =
    detail.distributionChainId ?? detail.depositChainId ?? null
  formData.distributionChain = distributionChainId
    ? CHAIN_NAME_BY_ID[distributionChainId as number] || ''
    : ''

  // Map deposit chain and stablecoin from API response
  const depositChainIdValue =
    detail.depositChainId ?? distributionChainId ?? null
  formData.depositChain = depositChainIdValue
    ? CHAIN_NAME_BY_ID[depositChainIdValue as number] || ''
    : ''
  formData.depositChainStablecoin = paymentToken.symbol || ''

  let pricingMethod: string = 'fixed'
  if (pricingBlob?.schemaId === 1) pricingMethod = 'flexible'
  else if (pricingBlob?.schemaId === 0) pricingMethod = 'fixed'
  else if (detail?.bespoke) pricingMethod = 'bespoke'

  formData.pricingMethod = pricingMethod

  const fdvFixed =
    pricingBlob?.schemaId === 0
      ? fromScaledValue(pricingPayload.fdvInWei, projectTokenDecimals)
      : ''
  formData.fdv = fdvFixed
  formData.maxRaise = pricingPayload.maxCapInWei
    ? fromScaledValue(pricingPayload.maxCapInWei, paymentTokenDecimals)
    : ''
  formData.minimumRaiseThreshold =
    detail.minRaiseThreshold !== undefined
      ? String(detail.minRaiseThreshold)
      : ''
  formData.minimumInvestmentPerUser =
    detail.minInvestmentPerUser !== undefined
      ? String(detail.minInvestmentPerUser)
      : ''

  formData.tokenSupply = detail.tokenSupply ?? ''
  formData.lockdropDuration = detail.lockdropDuration ?? ''
  formData.lockdropStartDate = detail.lockdropStartDate ?? ''
  formData.lockdropEndDate = detail.lockdropEndDate ?? ''
  formData.targetTVL =
    detail.targetTVL !== undefined ? String(detail.targetTVL) : ''
  formData.targetYieldProposal = detail.targetYieldProposal ?? ''
  formData.fdvLastRound =
    detail.fdvLastRound !== undefined ? String(detail.fdvLastRound) : ''

  formData.tokenPrice =
    pricingBlob?.schemaId === 0
      ? fromScaledValue(pricingPayload.tokenPriceInWei, paymentTokenDecimals)
      : ''
  formData.cliff =
    pricingBlob?.schemaId === 0
      ? secondsToMonthsString(pricingPayload.cliffSecs)
      : ''
  formData.linearVesting =
    pricingBlob?.schemaId === 0
      ? secondsToMonthsString(pricingPayload.vestingSecs)
      : ''
  formData.tgeRelease =
    pricingBlob?.schemaId === 0
      ? bipsToPercentString(pricingPayload.tgeUnlockBips)
      : ''
  formData.fixedPoolName =
    pricingBlob?.schemaId === 0
      ? pricingPayload.tierId || 'Genesis Pool'
      : 'Genesis Pool'
  formData.enableBuybackProtection =
    pricingPayload.enableBuybackProtection || false
  formData.buybackPaymentToken = pricingPayload.buybackPaymentToken || ''
  formData.buybackPrice = pricingPayload.buybackPrice || ''
  formData.fundingWallet = pricingPayload.fundingWallet || ''
  formData.buybackStartDate = pricingPayload.buybackStartDate || ''
  formData.buybackEndDate = pricingPayload.buybackEndDate || ''

  const tiers = Array.isArray(pricingPayload.tiers) ? pricingPayload.tiers : []
  formData.flexibleSchedules = tiers.map((tier: any, index: number) => ({
    id: tier.tierId || `tier-${index + 1}`,
    name: tier.tierId || `Tier ${index + 1}`,
    fdv: fromScaledValue(tier.fdvInWei, projectTokenDecimals),
    tokenPrice: fromScaledValue(tier.tokenPriceInWei, paymentTokenDecimals),
    cliff: secondsToMonthsString(tier.cliffSecs),
    tgeRelease: bipsToPercentString(tier.tgeUnlockBips),
    linearVesting: secondsToMonthsString(tier.vestingSecs),
    enableBuybackProtection: tier.enableBuybackProtection || false,
    buybackPaymentToken: tier.buybackPaymentToken || '',
    buybackPrice: tier.buybackPrice || '',
    fundingWallet: tier.fundingWallet || '',
    buybackStartDate: tier.buybackStartDate || '',
    buybackEndDate: tier.buybackEndDate || '',
  }))

  if (!formData.fdv && formData.flexibleSchedules.length > 0)
    formData.fdv = formData.flexibleSchedules[0].fdv
  if (!formData.tokenPrice && formData.flexibleSchedules.length > 0)
    formData.tokenPrice = formData.flexibleSchedules[0].tokenPrice

  if (!formData.tokenSupply && formData.fdv && formData.tokenPrice)
    formData.tokenSupply = deriveTokenSupply(formData.fdv, formData.tokenPrice)

  formData.projectTokenAddress = projectToken.address ?? ''
  formData.projectTokenSymbol = projectToken.symbol ?? ''
  formData.projectTokenName = projectToken.name ?? ''
  formData.projectTokenDecimals = projectToken.decimals
    ? String(projectToken.decimals)
    : ''

  formData.projectWallet = funds.projectReceivingWallet ?? ''

  if (normalizedSalesTypeFromOverview === LOCKDROPS_VALUE) {
    formData.hasAssetManager = funds.hasAssetManager ?? ''
    formData.assetManagerWallet = funds.assetManagerWallet ?? ''
    formData.lockdropCuratorWallet = funds.lockdropCuratorWallet ?? ''
    formData.lockdropPerformanceFeesType =
      funds.lockdropPerformanceFeesType ?? ''
    formData.lockdropPerformanceFeesStablecoin =
      funds.lockdropPerformanceFeesStablecoin ?? ''
    formData.lockdropPerformanceFeesToken =
      funds.lockdropPerformanceFeesToken ?? ''
    formData.lockdropManagementFeesType = funds.lockdropManagementFeesType ?? ''
    formData.lockdropManagementFeesStablecoin =
      funds.lockdropManagementFeesStablecoin ?? ''
    formData.lockdropManagementFeesToken =
      funds.lockdropManagementFeesToken ?? ''
  } else {
    formData.commissionType = funds.curatorCommissionType ?? ''
    formData.stablecoinCommission = bipsToPercentString(
      funds.curatorStablecoinFeeBips,
    )
    formData.tokenCommission = bipsToPercentString(funds.curatorTokenFeeBips)
    formData.curatorCommissionWallet = funds.curatorWallet ?? ''
    if (normalizedSalesTypeFromOverview === 'OTC Deal') {
      formData.otcSettlementWallet = funds.otcSettlementWallet ?? ''
    }
  }

  formData.curatorCommitmentAmount = fromScaledValue(
    funds.curatorCommitmentAmount,
    paymentTokenDecimals,
  )

  const curatorProofs = Array.isArray(funds.curatorProofs)
    ? funds.curatorProofs
    : []
  const txProof = curatorProofs.find((proof) => proof.type === 'onchain')
  const docProof = curatorProofs.find((proof) => proof.type === 'document')
  formData.transactionLink = txProof?.value ?? ''
  const supportingDocsUrl = docProof?.value ?? ''
  formData.supportingDocumentsUrl = supportingDocsUrl
  formData.supportingDocuments = createRemoteFileStub(
    supportingDocsUrl,
    'supporting-document.pdf',
    'application/pdf',
    docProof?.size ?? docProof?.bytes,
  )

  formData.flatCommissionRate =
    funds.curatorFeeBips !== undefined
      ? bipsToPercentString(funds.curatorFeeBips)
      : ''
  formData.carryCommissionRate = ''
  formData.curatorCommissionWallet = funds.curatorWallet ?? ''
  formData.commissionType = funds.curatorCommissionType ?? ''
  formData.stablecoinCommission =
    funds.curatorStablecoinFeeBips !== undefined
      ? bipsToPercentString(funds.curatorStablecoinFeeBips)
      : ''
  formData.tokenCommission =
    funds.curatorTokenFeeBips !== undefined
      ? bipsToPercentString(funds.curatorTokenFeeBips)
      : ''

  formData.secondaryMarketEnabled = Boolean(
    (detail as any).secondaryMarketEnabled,
  )

  const rawWithdrawalType = (detail as any).withdrawalType
  formData.withdrawalType =
    typeof rawWithdrawalType === 'number'
      ? rawWithdrawalType
      : Number.isFinite(Number(rawWithdrawalType))
      ? Number(rawWithdrawalType)
      : 0

  const royaltySplitSource =
    (nftMetadata.royaltySplit as any) ?? detail.royaltySplit ?? {}
  formData.royaltySplit = {
    project: bipsToPercentString(royaltySplitSource?.projectBips),
    curator: bipsToPercentString(royaltySplitSource?.curatorBips),
  }

  const depositStarts = timelines.find(
    (event) => event.type === 'depositStarts',
  )
  const depositEnds = timelines.find((event) => event.type === 'depositEnds')
  const nftClaiming = timelines.find((event) => event.type === 'nftClaiming')
  const firstUnlock = timelines.find((event) => event.type === 'firstUnlock')
  const vestingStarts = timelines.find(
    (event) => event.type === 'vestingStarts',
  )
  const nftTransferable = timelines.find(
    (event) => event.type === 'nftTransferable',
  )

  formData.purchaseStartDate = toDateTimeLocalString(depositStarts?.datetime)
  formData.purchaseEndDate = toDateTimeLocalString(depositEnds?.datetime)
  formData.mintStartDate = toDateTimeLocalString(nftClaiming?.datetime)
  formData.tgeTargetDate = toDateTimeLocalString(firstUnlock?.datetime)
  formData.vestingStartDate = toDateTimeLocalString(vestingStarts?.datetime)

  const tgeDate = formData.tgeTargetDate
    ? new Date(formData.tgeTargetDate + 'Z')
    : null
  let nftTransferability = 'never'
  let customStart = ''
  if (nftTransferable?.datetime) {
    const nftDate = new Date(nftTransferable.datetime)
    const customDateString = toDateTimeLocalString(nftTransferable.datetime)
    if (tgeDate && Math.abs(nftDate.getTime() - tgeDate.getTime()) < 2000)
      nftTransferability = 'immediate'
    else if (tgeDate) {
      const plusOneYear = new Date(tgeDate)
      plusOneYear.setUTCFullYear(plusOneYear.getUTCFullYear() + 1)
      if (Math.abs(nftDate.getTime() - plusOneYear.getTime()) < 2000)
        nftTransferability = 'after-year'
      else {
        nftTransferability = 'custom'
        customStart = customDateString
      }
    } else {
      nftTransferability = 'custom'
      customStart = customDateString
    }
  } else if (nftMetadata.nonTransferableUntil) {
    const timestamp = Number(nftMetadata.nonTransferableUntil) * 1000
    if (Number.isFinite(timestamp) && timestamp > 0) {
      nftTransferability = 'custom'
      customStart = toDateTimeLocalString(new Date(timestamp).toISOString())
    }
  }

  // The v1 deposit form edits the NFT-transferable milestone as a plain
  // date, so always expose it explicitly instead of a preset.
  if (isV1ContractDealType(formData.salesType) && nftTransferable?.datetime) {
    nftTransferability = 'custom'
    customStart = toDateTimeLocalString(nftTransferable.datetime)
  }

  formData.nftTransferability = nftTransferability
  formData.customStartDateTime = customStart

  formData.accessoryServices = (() => {
    const svc = detail.accessoryServices ?? {}
    const otherText = typeof svc.other === 'string' ? svc.other : ''
    const otherSelected = Boolean(otherText && otherText.trim())
    return {
      marketing: Boolean(svc.marketing),
      kolCampaigns: Boolean(svc.kolCampaigns),
      marketMaking: Boolean(svc.marketMaking),
      treasuryManagement: Boolean(svc.treasuryManagement),
      otcDeals: Boolean(svc.otcDeals),
      auditing: Boolean(svc.auditing),
      hrRecruitment: Boolean(svc.hrRecruitment),
      cexListing: Boolean(svc.cexListing),
      other: otherSelected,
      otherDescription: otherSelected ? otherText : '',
    }
  })()

  formData.referralEnabled = Boolean(referral.enabled)
  formData.referralGeneralDiscount =
    referral.general?.discount !== undefined
      ? String(referral.general.discount)
      : ''
  formData.referralGeneralKickback =
    referral.general?.kickback !== undefined
      ? String(referral.general.kickback)
      : ''
  const rawReferralEntries = Array.isArray(referral.customWallets)
    ? referral.customWallets
    : []
  formData.referralCustomEntries = rawReferralEntries.length
    ? rawReferralEntries.map((entry: any) => {
        const hasTemplateFields =
          entry &&
          (Object.prototype.hasOwnProperty.call(entry, 't2WalletAddress') ||
            Object.prototype.hasOwnProperty.call(entry, 't3WalletAddress'))

        if (hasTemplateFields) {
          return {
            promoTier: String(entry.promoTier || ''),
            t2WalletAddress: String(entry.t2WalletAddress || ''),
            t3WalletAddress: String(entry.t3WalletAddress || ''),
            discountPercentage: Number(entry.discountPercentage ?? 0),
            kickbackPercentage: Number(entry.kickbackPercentage ?? 0),
            promoCode: String(entry.promoCode || ''),
            t2SharePercentage: Number(entry.t2SharePercentage ?? 0),
            t3SharePercentage: Number(entry.t3SharePercentage ?? 0),
            contactName: String(entry.contactName || ''),
            contactTelegram: String(entry.contactTelegram || ''),
          }
        }

        const walletAddress = String(entry?.walletAddress || '')
        const tier = String(entry?.tierWalletType || '').toUpperCase()
        const isT3 = tier === 'T3'
        const shareValue = Number(entry?.sharePercentage ?? 0)
        const normalizedShare = Number.isFinite(shareValue) ? shareValue : 0

        return {
          promoTier: String(entry?.promoTier || ''),
          t2WalletAddress: isT3 ? '' : walletAddress,
          t3WalletAddress: isT3 ? walletAddress : '',
          discountPercentage: Number(entry?.discountPercentage ?? 0),
          kickbackPercentage: Number(entry?.kickbackPercentage ?? 0),
          promoCode: String(entry?.promoCode || ''),
          t2SharePercentage: isT3 ? 0 : normalizedShare,
          t3SharePercentage: isT3 ? normalizedShare : 0,
          contactName: String(entry?.contactName || ''),
          contactTelegram: String(
            entry?.contactTelegram || entry?.telegram || '',
          ),
        }
      })
    : []
  formData.referralCustomCsv = createReferralCsvStub(
    formData.referralCustomEntries,
    'custom-referrals.csv',
    referral.customCsvSize ?? referral.custom_csv_size,
  )

  formData.marketingGuidelinesAgreementAccepted =
    metadata.marketingGuidelinesAgreementAccepted ?? false
  formData.submittedAt =
    deal.submitted_at ?? deal.updated_at ?? deal.created_at ?? null
  formData.submissionStatus = normalizeStatus(deal.status)
  formData.submissionNotes = deal.submission_notes ?? ''
  formData.dealState = normalizeDealState(deal.state)
  formData.rejectReasons = deal.reject_reason ?? null

  return formData
}

export const postDeal = async (payload: any) => {
  const res = await fetch(ADMIN_DEALS_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`POST failed: ${res.status} ${text}`)
  }
  return res.json()
}

export const fetchDealById = async (dealId: string) => {
  const res = await fetch(`${ADMIN_DEALS_ENDPOINT}/${dealId}`, {
    method: 'GET',
    credentials: 'include',
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`GET failed: ${res.status} ${text}`)
  }
  const body = await res.json()
  return body.data ?? body
}

export const fetchDealFormData = async (dealId: string) => {
  const rawDeal = await fetchDealById(dealId)
  return mapDealResponseToFormData(rawDeal)
}

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype

export const deepMergeDealPayload = (existing: any, incoming: any): any => {
  if (incoming === undefined) return existing
  if (!isPlainObject(incoming)) return incoming
  if (!isPlainObject(existing)) return incoming
  const result: Record<string, any> = { ...existing }
  for (const key of Object.keys(incoming)) {
    result[key] = deepMergeDealPayload(existing[key], incoming[key])
  }
  return result
}

export const updateDeal = async (dealId: string, payload: any) => {
  const res = await fetch(`${ADMIN_DEALS_ENDPOINT}/${dealId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`PUT failed: ${res.status} ${text}`)
  }
  return res.json()
}

export const submitDeal = async (dealId: string) => {
  const res = await fetch(`${ADMIN_DEALS_ENDPOINT}/submit/${dealId}`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`POST submit failed: ${res.status} ${text}`)
  }
  return res.json()
}
